import React from "react";
import Toast from "react-native-toast-message";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Platform } from "react-native";
import { useNetworkStatus } from "@/network/NetworkContext";
import { runOfflineSync } from "./sync/gateway";
import { pullFailedToastText } from "./sync/hints";
import { getOfflineStore } from "./store";
import { isOfflineSyncPaused, onOfflineSyncResumed } from "./sync/syncPause";

export const PENDING_SYNC_RETRY_MS = 20_000;

function hasOpenOutbox(items: { status: string }[]): boolean {
  return items.some((item) => item.status === "pending" || item.status === "error");
}

export function OfflineSyncTrigger() {
  const { isOnline } = useNetworkStatus();
  const qc = useQueryClient();

  useEffect(() => {
    if (!isOnline) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      if (cancelled || isOfflineSyncPaused() || Platform.OS !== "web") return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void retryIfPending();
      }, PENDING_SYNC_RETRY_MS);
    };

    const syncNow = async (notifyFailures: boolean) => {
      if (cancelled || isOfflineSyncPaused()) return;
      const result = await runOfflineSync();
      if (cancelled) return;
      void qc.invalidateQueries({ queryKey: ["offline"] });
      void qc.invalidateQueries({ queryKey: ["care"] });
      void qc.invalidateQueries({ queryKey: ["cases"] });
      if (result.conflicts > 0 || result.quarantined > 0) {
        if (notifyFailures) {
          Toast.show({
            type: "error",
            text1: "Synchronisierung braucht eine Entscheidung",
            text2:
              result.conflicts > 0
                ? "Konflikt: lokaler Eintrag bleibt erhalten."
                : "Eintrag in Quarantäne, nicht gelöscht.",
          });
        }
      } else if (result.failed > 0) {
        if (result.errors.length > 0) {
          console.warn(
            "OfflineSyncTrigger: sync failed",
            result.errors.map((entry) => entry.error).filter(Boolean).join("; "),
          );
        }
        if (notifyFailures) {
          const count = result.failed;
          Toast.show({
            type: "error",
            text1: "Synchronisierung fehlgeschlagen",
            text2:
              count === 1
                ? "1 Eintrag wurde nicht synchronisiert."
                : `${count} Einträge wurden nicht synchronisiert.`,
          });
        }
      } else if (result.pullFailed && (notifyFailures || result.success > 0)) {
        Toast.show({
          type: result.success > 0 ? "success" : "info",
          text1: result.success > 0 ? "Synchronisiert" : "Aktualisierung unvollständig",
          text2: pullFailedToastText(result.success),
        });
      } else if (result.success > 0) {
        Toast.show({
          type: "success",
          text1: "Synchronisiert",
          text2: `${result.success} Vorgänge übertragen.`,
        });
      }
      const store = await getOfflineStore();
      const pending = (await store.getOutbox()).filter((i) => i.status === "pending");
      if (pending.length) {
        void qc.invalidateQueries({ queryKey: ["offline"] });
      }
    };

    const retryIfPending = async () => {
      try {
        const store = await getOfflineStore();
        const open = hasOpenOutbox(await store.getOutbox());
        if (cancelled || !open) return;
        await syncNow(false);
      } catch (err: unknown) {
        console.error(
          "OfflineSyncTrigger: sync failed",
          err instanceof Error ? err.message : err,
        );
      } finally {
        schedule();
      }
    };

    const unsubscribeResume = onOfflineSyncResumed(() => {
      if (cancelled || !isOnline) return;
      void syncNow(false).catch((err: unknown) => {
        console.error(
          "OfflineSyncTrigger: sync failed",
          err instanceof Error ? err.message : err,
        );
      });
      schedule();
    });

    void syncNow(true)
      .catch((err: unknown) => {
        console.error(
          "OfflineSyncTrigger: sync failed",
          err instanceof Error ? err.message : err,
        );
      })
      .finally(() => {
        schedule();
      });

    return () => {
      cancelled = true;
      unsubscribeResume();
      if (timer) clearTimeout(timer);
    };
  }, [isOnline, qc]);

  return null;
}
